import type { Competitor } from '../types'

// Sources (checked 2026-10-01): later.com/pricing (USD, taxes extra) and its
// page data, later.com/link-in-bio, help.later.com articles on the Link in Bio
// plans, design, featured media, analytics, social icons, and trials.
const later: Competitor = {
  slug: 'later',
  aliases: ['linkinbio', 'later-link-in-bio'],
  name: 'Later',
  tagline: 'Link in Bio (Linkin.bio) bundled with Later\'s social media scheduler.',
  site: 'https://later.com/link-in-bio/',
  pricingUrl: 'https://later.com/pricing/',
  checkedOn: '2026-10-01',
  seo: {
    title: 'Later Link in Bio alternative: mychar.gg vs Later (2026)',
    description: 'A Later Link in Bio (Linkin.bio) alternative you can design yourself. Compare design, music, video clips, custom domains, analytics, and price.'
  },
  hero: {
    title: 'The Later Link in Bio alternative you design yourself',
    description: 'Later\'s Linkin.bio comes with a scheduler. mychar.gg is the page itself, built in a live drag-and-drop editor with effects, music, and clips, from free to €6.99 once.'
  },
  summary: 'mychar.gg is a Later Link in Bio alternative for anyone who does not need a social media scheduler: you get far more design control, a music player, video clips, YouTube, Twitch, and Discord sections, and a custom domain option, starting free and €6.99 once for Pro. Later is the better fit if you already schedule posts with it and want a clickable Instagram and TikTok grid on your page.',
  chooseMychar: [
    'only need the link page, not a post scheduler',
    'want layouts, fonts, effects, and cursors that make the page yours',
    'want music, video clips, and your own domain',
    'would rather pay once than $18.75 a month or more'
  ],
  chooseThem: [
    'already schedule posts on Later',
    'want a shoppable grid of your Instagram and TikTok posts',
    'manage several brands with a team'
  ],
  features: {
    free_plan: { yes: true, text: 'In their help center, with a Later banner' },
    paid_plans: 'Starter $18.75, Growth $37.50, Scale $82.50 a month',
    billing: 'Monthly or yearly subscription',
    first_year: '$225 (Starter, billed yearly)',
    custom_domain: { yes: false, text: 'Not offered' },
    remove_branding: { yes: true, text: 'Starter and up' },
    editor: 'Dashboard forms with a page preview',
    drag_drop: { yes: true, text: 'Reorder blocks' },
    themes: { yes: true, text: 'A few premade themes' },
    fonts: 'One font from a preset list',
    effects: { yes: false, text: 'Color or image background' },
    video_background: { yes: false, text: 'Color or image only' },
    links: { yes: true, text: 'Buttons and button groups' },
    video_clips: { yes: false, text: 'One featured YouTube video' },
    youtube: 'One featured video',
    twitch: { yes: false, text: 'Not listed' },
    discord: { yes: false, text: 'Not listed' },
    analytics_free: { yes: true, text: 'Last 3 months' },
    analytics_history: '3 months free, 1 to 2 years paid',
    analytics_live: 'Views by location on Growth',
    store: { yes: false, text: 'Product links, no checkout' },
    email_capture: { yes: true, text: 'Mailchimp form' },
    scheduling: { yes: true, text: 'Links go live with scheduled posts' },
    mobile_app: { yes: true, text: 'iOS and Android' }
  },
  plans: [
    { name: 'Free', price: '$0', period: 'forever', note: 'One page with a Later banner and 3 months of analytics (listed in Later\'s help center).' },
    { name: 'Starter', price: '$18.75', period: '/month, billed yearly', note: 'Or $25 monthly. No Later branding, custom SEO, a year of analytics.' },
    { name: 'Growth', price: '$37.50', period: '/month, billed yearly', note: 'Or $50 monthly. Two pages and views by location.' },
    { name: 'Scale', price: '$82.50', period: '/month, billed yearly', note: 'Or $110 monthly. Six pages and two years of analytics.' }
  ],
  plansNote: 'USD, taxes extra. Paid plans include Later\'s social media scheduler, and each has a 14-day trial that needs a card.',
  advantages: [
    {
      title: 'Real design control',
      description: 'Four layouts, eight fonts plus uploads on Elite, video backgrounds, card styles, and animated effects. Later offers a few themes, one font, and a color or image background.',
      icon: 'i-lucide-palette'
    },
    {
      title: 'Your own domain',
      description: 'Connect a domain you own for €2.99 per month on any plan. Later pages live on linkin.bio.',
      icon: 'i-lucide-globe'
    },
    {
      title: 'Music and video on the page',
      description: 'A music player on every plan and up to 18 video clips that play in place. Later shows one featured YouTube video.',
      icon: 'i-lucide-music'
    },
    {
      title: 'YouTube, Twitch, and Discord',
      description: 'Sections that show your latest uploads, your live status and schedule, and your Discord profile.',
      icon: 'i-lucide-radio'
    },
    {
      title: 'Pay once',
      description: 'Pro is €6.99 once. Removing the Later banner takes Starter at $225 a year billed yearly.',
      icon: 'i-lucide-badge-euro'
    },
    {
      title: 'Comments and community',
      description: 'Visitor comments, linked friends, achievements, and a view counter.',
      icon: 'i-lucide-message-circle'
    }
  ],
  strengths: [
    {
      title: 'A scheduler included',
      description: 'Every paid plan includes Later\'s social media scheduler for eight networks, and links you add while scheduling go live on your page with the post.'
    },
    {
      title: 'A shoppable post grid',
      description: 'Your page can show a clickable grid of Instagram and TikTok posts, with up to five links per Instagram post. mychar.gg has no post grid.'
    },
    {
      title: 'Marketing analytics',
      description: 'Post-level clicks, UTM tracking, and Google Analytics sit in the same suite as your social analytics.'
    },
    {
      title: 'Teams and brands',
      description: 'Higher plans add extra pages, users, and approval workflows for teams that manage several brands.'
    }
  ],
  sections: [
    {
      title: 'Why people look for a Later Link in Bio alternative',
      paragraphs: [
        'Later\'s Link in Bio, formerly Linkin.bio, is part of a social media scheduling suite. That is its strength and its catch. If you schedule posts on Later every week, the page comes with it. If you only want a good link page, you are paying for a scheduler: removing the Later banner starts with Starter at $18.75 a month billed yearly.',
        'The design range is narrow too. You get a few premade themes, one font from a preset list, and a color or image background. There is no custom domain, no video background, and Twitch and Discord are not among the supported social icons.'
      ]
    },
    {
      title: 'How mychar.gg is different',
      paragraphs: [
        'mychar.gg is only the page, so all of the effort goes into it. You design it in an [editor](/docs/customization) that is your page in edit mode, dragging sections, links, and widgets into place and choosing layouts, fonts, colors, backgrounds, animated effects, and cursors while everything updates live. Content plays on the page itself: a music player, video clips, and YouTube and Twitch sections that keep themselves current.',
        'There is no scheduler and no post grid, so if those are why you use Later, stay there. If they are not, the price difference is large: mychar.gg is free to start, Pro is €6.99 once, Elite €12.99 once, and a custom domain is an optional €2.99 per month. The [guide for Instagram](/for/instagram) covers adding the link to your profile.'
      ]
    }
  ],
  switchSteps: [
    { title: 'Claim your username', description: 'Sign up free at [mychar.gg/signup](/signup) and pick the name for mychar.gg/yourname.' },
    { title: 'Copy your buttons', description: 'Add your platforms from the built-in list, unlimited on every plan. Shop and product links become custom links, available on Pro and Elite.' },
    { title: 'Design the page', description: 'Pick a layout, fonts, colors, and a background, or start from a community [skin](/skins).' },
    { title: 'Add media', description: 'Upload a music track and your best clips, and connect YouTube and Twitch.' },
    { title: 'Swap the link in your Instagram bio', description: 'Replace your linkin.bio URL. You can keep scheduling posts on Later; the bio link simply points to mychar.gg.' }
  ],
  faq: [
    {
      label: 'Can I keep using Later for scheduling?',
      content: 'Yes. Later\'s scheduler works on its own. Point your Instagram and TikTok bio links at your mychar.gg page instead of linkin.bio.'
    },
    {
      label: 'Does mychar.gg have a shoppable Instagram grid?',
      content: 'No. mychar.gg does not import your posts. You can feature products with custom links on Pro and Elite, and add video clips that play on the page.'
    },
    {
      label: 'Is mychar.gg cheaper than Later?',
      content: 'For the link page alone, yes. mychar.gg Pro is €6.99 once. Later\'s paid plans start at $18.75 a month billed yearly, but they include a social media scheduler.'
    },
    {
      label: 'Can I use my own domain?',
      content: 'Yes, with the custom domain add-on for €2.99 per month on any plan. Later pages stay on linkin.bio. See the [custom domain guide](/docs/profile/custom-domain).'
    }
  ]
}

export default later
